import CloseIcon from "@mui/icons-material/Close";
import {
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";

import { useSubscriptionStatus } from "hooks";
import { shipperSubscriptionTierWithoutPrivateShipments } from "utils";
import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { ShipperUpgradeNowButton } from "../ShipperUpgradeNowButton";

interface Props {
  currency: string;
  price: string;
  setCurrency: (newValue: string) => void;
  setPrice: (newPrice: string) => void;
  shipmentType?: ShipmentType;
  quotingUnitOfMeasure: UnitOfMeasureType;
  onClose: VoidFunction;
  autoFocus?: boolean;
}

export function BookNowMenu(props: Props) {
  const {
    currency,
    price,
    setCurrency,
    setPrice,
    shipmentType,
    quotingUnitOfMeasure,
    onClose,
    autoFocus = false,
  } = props;

  const { subscription } = useSubscriptionStatus();

  const showUpgradePrompt =
    subscription != null &&
    shipperSubscriptionTierWithoutPrivateShipments(subscription);

  if (subscription == null) {
    return null;
  }

  const isProject = shipmentType === "MULTI_SHIPMENT_PROJECT";
  const isNotAllIn = quotingUnitOfMeasure !== "ALL_IN";

  const getPriceLabel = () => {
    if (isNotAllIn) {
      const unitLabel = formatQuotingUnitOfMeasure(quotingUnitOfMeasure, false);
      if (isProject) {
        return `Price ${unitLabel} (per load)`;
      }
      return `Price ${unitLabel}`;
    }
    if (isProject) {
      return "Price per load";
    }
    return "Price";
  };

  return (
    <Stack direction="column" spacing={1}>
      <InputBase
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder={getPriceLabel()}
        disabled={showUpgradePrompt}
        autoFocus={autoFocus}
        fullWidth
        startAdornment={<InputAdornment position="start">$</InputAdornment>}
        endAdornment={
          <InputAdornment position="end" sx={{ gap: 1 }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 28, alignSelf: "center" }}
            />
            <Select
              value={currency ?? "CAD"}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={showUpgradePrompt}
              variant="standard"
              sx={{
                minWidth: 56,
                "&::before": { display: "none" },
                "&::after": { display: "none" },
              }}
            >
              <MenuItem value="CAD">CAD</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
            <Tooltip title="Close Book Now">
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close Book Now"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        }
      />
      {showUpgradePrompt && (
        <ShipperUpgradeNowButton
          buttonSize="small"
          trackingSource="create_shipment_form:book_now_paywall"
        />
      )}
    </Stack>
  );
}
